/** DataTypeInspector — interprets selected bytes as common data types. */
import React, { useMemo } from 'react';

import type { ByteRange } from './useByteSelection';

interface Props {
  bytes: Uint8Array;
  selection: ByteRange | null;
}

function hexString(buf: Uint8Array): string {
  return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join(' ');
}

function binaryString(buf: Uint8Array): string {
  return Array.from(buf, (b) => b.toString(2).padStart(8, '0')).join(' ');
}

function tryUtf8(buf: Uint8Array): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buf);
  } catch {
    return '<invalid UTF-8>';
  }
}

type Row = { label: string; value: string };

function interpret(slice: Uint8Array): Row[] {
  const len = slice.length;
  const rows: Row[] = [];

  rows.push({ label: 'Hex', value: hexString(slice) });
  rows.push({ label: 'Binary', value: binaryString(slice) });
  rows.push({ label: 'UTF-8', value: tryUtf8(slice) });

  // Build a DataView from a copy (ensures aligned buffer)
  const ab = new ArrayBuffer(Math.max(len, 8));
  const view = new DataView(ab);
  for (let i = 0; i < len; i++) view.setUint8(i, slice[i]);

  if (len >= 1) {
    rows.push({ label: 'UInt8', value: String(view.getUint8(0)) });
    rows.push({ label: 'Int8', value: String(view.getInt8(0)) });
  }
  if (len >= 2) {
    rows.push({ label: 'UInt16 LE', value: String(view.getUint16(0, true)) });
    rows.push({ label: 'UInt16 BE', value: String(view.getUint16(0, false)) });
    rows.push({ label: 'Int16 LE', value: String(view.getInt16(0, true)) });
    rows.push({ label: 'Int16 BE', value: String(view.getInt16(0, false)) });
  }
  if (len >= 4) {
    rows.push({ label: 'UInt32 LE', value: String(view.getUint32(0, true)) });
    rows.push({ label: 'UInt32 BE', value: String(view.getUint32(0, false)) });
    rows.push({ label: 'Int32 LE', value: String(view.getInt32(0, true)) });
    rows.push({ label: 'Int32 BE', value: String(view.getInt32(0, false)) });
    rows.push({ label: 'Float32 LE', value: String(view.getFloat32(0, true)) });
    rows.push({ label: 'Float32 BE', value: String(view.getFloat32(0, false)) });
  }
  if (len >= 8) {
    rows.push({ label: 'UInt64 LE', value: String(view.getBigUint64(0, true)) });
    rows.push({ label: 'UInt64 BE', value: String(view.getBigUint64(0, false)) });
    rows.push({ label: 'Int64 LE', value: String(view.getBigInt64(0, true)) });
    rows.push({ label: 'Int64 BE', value: String(view.getBigInt64(0, false)) });
    rows.push({ label: 'Float64 LE', value: String(view.getFloat64(0, true)) });
    rows.push({ label: 'Float64 BE', value: String(view.getFloat64(0, false)) });
  }

  return rows;
}

export const DataTypeInspector: React.FC<Props> = ({ bytes, selection }) => {
  const rows = useMemo(() => {
    if (!selection) return null;
    const slice = bytes.slice(selection.start, selection.end + 1);
    if (!slice.length) return null;
    return interpret(slice);
  }, [bytes, selection]);

  if (!rows || !selection) return null;

  const count = selection.end - selection.start + 1;

  return (
    <div className="border-t border-border bg-muted/60 px-3 py-1.5 font-mono text-xs select-none overflow-x-auto">
      <div className="flex items-center gap-3 mb-1">
        <span className="text-muted-foreground font-semibold">
          {count} byte{count !== 1 ? 's' : ''} selected
        </span>
        <span className="text-muted-foreground text-[10px]">
          0x{selection.start.toString(16).padStart(4, '0')}
          {count > 1 && ` – 0x${selection.end.toString(16).padStart(4, '0')}`}
        </span>
      </div>
      <div className="grid gap-x-4 gap-y-0.5 grid-cols-[auto_1fr]">
        {rows.map((r) => (
          <React.Fragment key={r.label}>
            <span className="text-muted-foreground whitespace-nowrap">{r.label}</span>
            <span className="text-foreground truncate" title={r.value}>{r.value}</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
